/**
 * OIDC-Adapter (Authentik) — Ersatz fuer `@logto/react`.
 *
 * Wir bilden hier die von der App genutzte Logto-API (useLogto,
 * useHandleSignInCallback) auf `react-oidc-context` + `oidc-client-ts` ab,
 * damit die ~15 Aufrufstellen fast unveraendert bleiben. Authentik bringt
 * eine eigene Hosted-Login-Seite mit, daher entfaellt jede Custom-Login-UI.
 *
 * Config-Zufluss ueber Runtime `window.__ENV__` (siehe `@/lib/runtime-env`):
 * OIDC_ISSUER / OIDC_CLIENT_ID aus der Server-.env.
 */

import { ReactNode, useCallback, useEffect, useRef } from "react"
import {
  AuthProvider as OidcContextProvider,
  useAuth,
  type AuthProviderProps,
} from "react-oidc-context"
import { WebStorageStateStore } from "oidc-client-ts"

import { OIDC_ISSUER, OIDC_CLIENT_ID } from "@/lib/runtime-env"

// Argument-Form, die die App an signIn() uebergibt (Logto-Altlast). Bei
// Authentik ist redirect_uri fix (Provider-Config), daher wird das Argument
// ignoriert — der Typ bleibt nur der Aufwaertskompatibilitaet halber erhalten.
type SignInArg = string | { redirectUri?: string; postRedirectUri?: string; [key: string]: unknown }

const oidcConfig: AuthProviderProps = {
  authority: OIDC_ISSUER,
  client_id: OIDC_CLIENT_ID,
  redirect_uri: window.location.origin + "/callback",
  post_logout_redirect_uri: window.location.origin,
  scope: "openid profile email offline_access",
  response_type: "code",
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // react-oidc-context verarbeitet den /callback-Redirect automatisch; danach
  // die Auth-Parameter aus der URL entfernen. Der finale Redirect passiert in
  // der Callback-Seite via useHandleSignInCallback().
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname)
  },
}

/**
 * App-weiter Auth-Provider. Ersetzt Logtos `<LogtoProvider>`.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  return <OidcContextProvider {...oidcConfig}>{children}</OidcContextProvider>
}

/**
 * Logto-kompatibler Hook. Bildet die genutzte Teilmenge der Logto-API nach.
 */
export function useLogto() {
  const auth = useAuth()

  const getAccessToken = useCallback(
    // resource-Argument wird bei Authentik nicht gebraucht -> ignoriert.
    async (_resource?: string): Promise<string | undefined> => auth.user?.access_token,
    [auth.user],
  )

  const getIdTokenClaims = useCallback(
    async () => auth.user?.profile,
    [auth.user],
  )

  const signIn = useCallback(
    // Argument wird ignoriert (redirect_uri ist fix in der Provider-Config).
    (_arg?: SignInArg) => auth.signinRedirect(),
    [auth],
  )

  const signOut = useCallback(
    (postLogoutUri?: string) =>
      auth.signoutRedirect(
        postLogoutUri ? { post_logout_redirect_uri: postLogoutUri } : undefined,
      ),
    [auth],
  )

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    signIn,
    signOut,
    getAccessToken,
    getIdTokenClaims,
    fetchUserInfo: getIdTokenClaims,
  }
}

/**
 * Ersatz fuer Logtos `useHandleSignInCallback`. react-oidc-context verarbeitet
 * den Redirect automatisch; wir warten nur auf `isAuthenticated` und rufen dann
 * `onSuccess` genau einmal auf.
 */
export function useHandleSignInCallback(onSuccess?: () => void) {
  const auth = useAuth()
  const calledRef = useRef(false)

  useEffect(() => {
    if (auth.isAuthenticated && !calledRef.current) {
      calledRef.current = true
      onSuccess?.()
    }
  }, [auth.isAuthenticated, onSuccess])

  return {
    error: auth.error,
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
  }
}

export default AuthProvider
